/**
 * Cart Success Fix
 * Fixes false error messages when items are successfully added to cart
 *
 * ISSUE: product-form.js checks `if (response.status)` which triggers error handler
 * even for successful responses (status 200, 201, etc.)
 *
 * SOLUTION: Intercept and properly validate response status codes
 * Only show errors for actual error status codes (4xx, 5xx)
 *
 * Version: 1.0
 */

(function() {
  'use strict';

  console.log('🔧 Cart Success Fix: Initializing...');

  // Wait for DOM and product-form to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 100); // Small delay to ensure product-form loads first
  }

  function init() {
    patchProductForm();
    monitorCartResponses();
    console.log('✅ Cart Success Fix: Complete');
  }

  /**
   * Patch product-form to correctly handle success responses
   */
  function patchProductForm() {
    // Wait for product-form to be defined
    if (!customElements.get('product-form')) {
      console.log('⏳ Waiting for product-form...');
      setTimeout(patchProductForm, 100);
      return;
    }

    // Get all product forms
    const forms = document.querySelectorAll('product-form');

    if (forms.length === 0) {
      console.log('ℹ️ No product forms found on this page');
      return;
    }

    console.log(`✅ Found ${forms.length} product form(s)`);

    // Patch the submit handler for each form
    forms.forEach(form => {
      if (form._successFixPatched) return; // Already patched
      form._successFixPatched = true;

      const originalOnSubmit = form.onSubmitHandler;

      form.onSubmitHandler = function(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage(); // Clear any previous errors

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
          method: 'POST',
          headers: {
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          }
        };

        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        console.log('📤 Submitting to cart...');

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => {
            console.log('📥 Response status:', response.status, response.statusText);

            // Store the actual HTTP status
            const httpStatus = response.status;

            return response.json().then(data => ({
              httpStatus: httpStatus,
              data: data
            }));
          })
          .then(({ httpStatus, data }) => {
            console.log('📦 Response data:', data);

            // CRITICAL FIX: Check HTTP status code, not response.status field
            // Success: 200-299
            // Client errors: 400-499 (user error, out of stock, etc.)
            // Server errors: 500-599

            const isError = httpStatus >= 400 ||
                           (data.status && (
                             data.status === 422 ||
                             data.status === 'error' ||
                             data.status >= 400
                           )) ||
                           data.message ||
                           data.description;

            if (isError) {
              console.error('❌ Cart error detected:', {
                httpStatus,
                responseStatus: data.status,
                message: data.message,
                description: data.description
              });

              // Publish error event
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: data.errors || data.description,
                  message: data.message,
                });
              }

              // Show error message
              this.handleErrorMessage(data.description || data.message || 'Unable to add item to cart');

              // Handle sold out message
              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText?.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }

              this.error = true;
              return;
            }

            // SUCCESS! 🎉
            console.log('✅ Item added to cart successfully!');
            this.error = false;

            // If no cart UI, redirect to cart page
            if (!this.cart) {
              console.log('🔄 Redirecting to cart...');
              window.location = window.routes.cart_url;
              return;
            }

            // Publish success event
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: data,
              });
            }

            // Handle quick add modal
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(data);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              // Render cart contents (drawer or notification)
              this.cart.renderContents(data);
            }
          })
          .catch((error) => {
            console.error('❌ Network error:', error);
            this.handleErrorMessage('Unable to add item to cart. Please check your connection.');
            this.error = true;
          })
          .finally(() => {
            // Reset button state
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      };

      console.log('✅ Patched product form:', form);
    });
  }

  /**
   * Monitor cart responses globally
   */
  function monitorCartResponses() {
    // Listen for cartUpdate events (success)
    document.addEventListener('cart:update', (event) => {
      console.log('🛒 cart:update event fired (SUCCESS)', event.detail);
    });

    // Listen for cartError events
    document.addEventListener('cart:error', (event) => {
      console.error('❌ cart:error event fired', event.detail);
    });

    console.log('✅ Cart event monitoring active');
  }

  /**
   * Helper: Check if response is actually an error
   */
  window.isCartError = function(response, httpStatus) {
    // HTTP status is the source of truth
    if (httpStatus && httpStatus >= 400) return true;

    // Check for explicit error indicators
    if (response.status === 422) return true; // Unprocessable entity (Shopify error)
    if (response.status === 'error') return true;
    if (response.message && !response.items) return true; // Error message without items
    if (response.description && !response.items) return true; // Error description without items

    // Success indicators
    if (response.items && Array.isArray(response.items)) return false;
    if (response.item_count !== undefined) return false;
    if (httpStatus >= 200 && httpStatus < 300) return false;

    // Default: not an error if we can't determine
    return false;
  };

  /**
   * Debug helper
   */
  window.testCartResponse = function(response, httpStatus = 200) {
    console.group('🧪 Testing Cart Response');
    console.log('HTTP Status:', httpStatus);
    console.log('Response:', response);
    console.log('Is Error?', window.isCartError(response, httpStatus));
    console.groupEnd();
  };

})();
